export const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DEFAULT_DOC_NAME = 'Untitled copy';
// Editor sizes are px; docx run sizes are half-points (1px = 0.75pt).
const PX_TO_HALF_POINTS = 1.5;

export function docFilename(name) {
  const safe = String(name || '').replace(/[\\/:*?"<>|]/g, '-').trim() || DEFAULT_DOC_NAME;
  return `${safe}.docx`;
}

export function docHasContent(delta) {
  return !!delta && delta.ops.some(op => typeof op.insert !== 'string' || op.insert.trim() !== '');
}

// Quill puts block formats (header, list, table) on the "\n" that ends each line.
function deltaToLines(delta) {
  const lines = [];
  let runs = [];
  delta.ops.forEach(op => {
    if (typeof op.insert !== 'string') return;
    const parts = op.insert.split('\n');
    parts.forEach((text, index) => {
      if (text) runs.push({ text, attributes: op.attributes || {} });
      if (index < parts.length - 1) {
        lines.push({ runs, attributes: op.attributes || {} });
        runs = [];
      }
    });
  });
  if (runs.length) lines.push({ runs, attributes: {} });
  return lines;
}

function toTextRun({ text, attributes }, { TextRun, UnderlineType }) {
  const size = parseInt(attributes.size, 10);
  return new TextRun({
    text,
    bold: !!attributes.bold,
    italics: !!attributes.italic,
    underline: attributes.underline ? { type: UnderlineType.SINGLE } : undefined,
    size: size > 0 ? Math.round(size * PX_TO_HALF_POINTS) : undefined,
  });
}

function toParagraph(line, docx, listInstance) {
  const { Paragraph, HeadingLevel } = docx;
  const headings = [HeadingLevel.HEADING_1, HeadingLevel.HEADING_2, HeadingLevel.HEADING_3];
  const { header, list } = line.attributes;
  const options = { children: line.runs.map(run => toTextRun(run, docx)) };
  if (header) options.heading = headings[header - 1];
  if (list === 'bullet') options.bullet = { level: 0 };
  if (list === 'ordered') options.numbering = { reference: 'ordered-list', level: 0, instance: listInstance };
  return new Paragraph(options);
}

function toTable(cellLines, docx) {
  const { Table, TableRow, TableCell, Paragraph, WidthType } = docx;
  const rows = [];
  cellLines.forEach(line => {
    const last = rows[rows.length - 1];
    if (last && last.id === line.attributes.table) last.cells.push(line);
    else rows.push({ id: line.attributes.table, cells: [line] });
  });
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map(row => new TableRow({
      children: row.cells.map(cell => new TableCell({
        children: [new Paragraph({ children: cell.runs.map(run => toTextRun(run, docx)) })],
      })),
    })),
  });
}

export async function deltaToDocxBlob(delta) {
  const docx = await import('docx');
  const lines = deltaToLines(delta);
  const children = [];
  let listInstance = 0;
  let previousList = null;
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    if (line.attributes.table) {
      const cells = [];
      while (index < lines.length && lines[index].attributes.table) cells.push(lines[index++]);
      index--;
      children.push(toTable(cells, docx));
      previousList = null;
      continue;
    }
    // A new instance restarts numbering for each separate ordered list.
    if (line.attributes.list === 'ordered' && previousList !== 'ordered') listInstance++;
    previousList = line.attributes.list || null;
    children.push(toParagraph(line, docx, listInstance));
  }
  // Word needs a paragraph after a closing table.
  if (children[children.length - 1] instanceof docx.Table) children.push(new docx.Paragraph({}));
  const document = new docx.Document({
    // docx's built-in heading styles are blue; keep them black like the editor.
    styles: {
      default: {
        heading1: { run: { color: '000000', bold: true, size: 45 }, paragraph: { spacing: { before: 0, after: 360 } } },
        heading2: { run: { color: '000000', bold: true, size: 30 }, paragraph: { spacing: { before: 480, after: 240 } } },
        heading3: { run: { color: '000000', bold: true, size: 27 }, paragraph: { spacing: { before: 420, after: 120 } } },
      },
    },
    numbering: {
      config: [{
        reference: 'ordered-list',
        levels: [{ level: 0, format: docx.LevelFormat.DECIMAL, text: '%1.', alignment: docx.AlignmentType.START }],
      }],
    },
    sections: [{ children }],
  });
  return docx.Packer.toBlob(document);
}
